import { Component, OnInit, HostListener, ElementRef, TemplateRef, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HeaderComponent } from '../../header/header.component';
import { PageHeaderComponent } from '../../page-header/page-header.component';
import { ProviderSidebarComponent } from '../../../../provider/sidebar/provider-sidebar.component';
import { OrgAdminSidebarComponent } from '../../../../org-admin/sidebar/org-admin-sidebar.component';
import { ModalComponent } from '../../primeng/modal/modal.component';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { ToasterService } from '../../../../services/toaster.service';
import { StepperComponent, StepperStep } from '../../primeng';
import { SftpUploadComponent } from './sftp-upload/sftp-upload.component';
import { ApiIntegrationUploadComponent } from './api-integration-upload/api-integration-upload.component';
import { CatalogueApiService, AuthApiService, FileApiService } from '@angular-workspace/services';
import { buildAssetCataloguePayloadWithAuth } from '../../../utils/asset-catalogue-payload.util';
import { resolveVerifiedUploadRequestType } from '@angular-workspace/feature';

@Component({
  selector: 'app-provider-upload',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    HeaderComponent,
    ProviderSidebarComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    ModalComponent,
    ConfirmationDialogComponent,
    StepperComponent,
    SftpUploadComponent,
    ApiIntegrationUploadComponent,
  ],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.scss',
})
export class ProviderUploadComponent implements OnInit, AfterViewInit {

  get isOrgAdminRoute(): boolean {
    return this.router.url.includes('/org-admin/');
  }

  currentStep = 3; // Step 4 (0-based index) - Upload files
  totalSteps = 4;

  @ViewChild('step4Template', { static: true }) step4Template!: TemplateRef<any>;

  stepperSteps: StepperStep[] = [
    {
      key: 'step1',
      label: 'Setup your datasets',
    },
    {
      key: 'step2',
      label: 'Enter Metadata',
    },
    {
      key: 'step3',
      label: 'Additional Info',
    },
    {
      key: 'step4',
      label: 'Upload files',
    },
  ];

  /** Step 1 label from route: "Setup your Models" on add-model, "Setup your datasets" on add-dataset */
  get stepperStepsWithLabel(): StepperStep[] {
    const label = this.router.url.includes('add-model') ? 'Setup your Models' : 'Setup your datasets';
    return this.stepperSteps.map((step, i) => i === 0 ? { ...step, label } : step);
  }

  selectedUploadOption: string = 'upload-file';
  selectedFiles: File[] = [];
  uploadedFiles: Array<{
    file: File;
    progress: number;
    status: 'pending' | 'uploading' | 'completed' | 'error';
    error?: string;
    previewData?: any;
    uploadId?: string;
    key?: string;
  }> = [];
  
  // Preview modal state
  previewModalOpen = false;
  previewingFile: any = null;
  uploading = false;
  dragActive = false;
  isUploadSuccessModalOpen = false;
  isPublishSuccessModalOpen = false;
  isDirectPushSuccessModalOpen = false; // Track Direct Push success modal state
  isPublishing = false;
  isPublished = false; // Track if publish was successful
  createdDatasetId: string | null = null; // Store the created dataset ID for navigation
  requestType: 'SFTP' | 'API' | null = null; // Track the type of request (SFTP or API Integration)
  
  // Published files preview
  publishedFilesPreview: { headers: string[], rows: any[][] } | null = null;
  sftpForm!: FormGroup;
  apiIntegrationForm!: FormGroup;

  // Dropdown options
  httpMethodOptions = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
  authenticationTypeOptions = ['Bearer Token', 'API Key', 'Basic Auth', 'OAuth 2.0', 'Custom'];
  responseFormatOptions = ['JSON', 'XML', 'CSV', 'Text'];

  // Dropdown states
  isHttpMethodDropdownOpen = false;
  isAuthenticationTypeDropdownOpen = false;
  isResponseFormatDropdownOpen = false;

  uploadOptions = [
    {
      value: 'upload-file',
      label: 'Upload File',
      description: 'Upload your data files directly through the web interface. Supported file types are based on the format selected in Step 3, along with CSV and TXT.',
      icon: 'upload'
    },
    {
      value: 'sftp-upload',
      label: 'SFTP Upload',
      description: 'Securely transfer your data files using SFTP protocol. Configure your SFTP credentials and automate file transfers.',
      icon: 'sftp'
    },
    {
      value: 'api-integration',
      label: 'API Integration',
      description: 'Integrate with external APIs to automatically fetch and sync data. Set up API endpoints and authentication.',
      icon: 'api'
    },
    {
      value: 'direct-push',
      label: 'Direct Push',
      description: 'Push data directly to the platform using our API. Ideal for real-time data streaming and automated updates.',
      icon: 'push'
    }
  ];

  isCreatingItem = false; // Flag to prevent multiple API calls

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private toasterService: ToasterService,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private catalogueApiService: CatalogueApiService,
    private authApiService: AuthApiService,
    private fileApiService: FileApiService
  ) {}

  /**
   * Get the localStorage key based on current route
   */
  private getLocalStorageKey(): string {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/add-model/')) {
      return 'modelFormData';
    }
    return 'datasetFormData'; // Default to dataset
  }

  /** File format chosen in step 3 (Additional Info). Used in step 4 to restrict uploads. */
  getStep3FileFormat(): string {
    const key = this.getLocalStorageKey();
    const raw = localStorage.getItem(key);
    if (!raw) return '';
    try {
      const data = JSON.parse(raw);
      return (data?.fileFormat || '').trim();
    } catch {
      return '';
    }
  }

  /**
   * Map format label (from step 3) to file extension(s). Always allow .csv and .txt in addition.
   */
  private formatToExtensions(format: string): string[] {
    const normalized = (format || '').trim().toLowerCase();
    if (!normalized) return ['.csv', '.txt'];
    const map: { [key: string]: string[] } = {
      csv: ['.csv'],
      tsv: ['.tsv'],
      json: ['.json'],
      xml: ['.xml'],
      xlsx: ['.xlsx'],
      parquet: ['.parquet'],
      avro: ['.avro'],
      pdf: ['.pdf'],
      docx: ['.docx'],
      txt: ['.txt'],
      png: ['.png'],
      jpg: ['.jpg', '.jpeg'],
      jpeg: ['.jpg', '.jpeg'],
      mp4: ['.mp4'],
      mp3: ['.mp3'],
      yaml: ['.yaml', '.yml'],
      yml: ['.yaml', '.yml'],
      jsonl: ['.jsonl']
    };
    const fromMap = map[normalized];
    const chosen = fromMap ? [...fromMap] : ['.' + normalized];
    const allowed = new Set<string>([...chosen, '.csv', '.txt']);
    return Array.from(allowed);
  }

  /** Allowed extensions for upload: step 3 format + CSV + TXT. */
  getAllowedExtensions(): string[] {
    const format = this.getStep3FileFormat();
    return this.formatToExtensions(format);
  }

  /** Accept attribute for the file input (e.g. ".csv,.txt,.tsv"). */
  getAcceptAttribute(): string {
    return this.getAllowedExtensions().join(',');
  }

  /** Human-readable allowed file types for the hint (e.g. "TXT, CSV, TSV"). */
  getAllowedFileTypesLabel(): string {
    const exts = this.getAllowedExtensions();
    const labels = exts.map(e => e.replace(/^\./, '').toUpperCase());
    const unique = Array.from(new Set(labels));
    return unique.sort((a, b) => a.localeCompare(b)).join(', ');
  }

  private getFileExtension(name: string): string {
    const i = name.lastIndexOf('.');
    return i >= 0 ? '.' + name.slice(i + 1).toLowerCase() : '';
  }

  private isAllowedFile(file: File): boolean {
    const ext = this.getFileExtension(file.name);
    return this.getAllowedExtensions().some(e => e.toLowerCase() === ext);
  }

  ngOnInit(): void {
    // Initialize SFTP form
    this.sftpForm = this.fb.group({
      sftpHost: ['', [Validators.required]],
      port: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
      username: ['', [Validators.required]],
      password: ['', [Validators.required]],
      remotePath: ['', [Validators.required]],
      localFilePath: ['', [Validators.required]],
      optionalNotes: ['']
    });

    // Initialize API Integration form
    this.apiIntegrationForm = this.fb.group({
      dataSourceName: ['', [Validators.required]],
      apiBaseUrl: ['', [Validators.required]],
      endpointPath: ['', [Validators.required]],
      httpMethod: ['', [Validators.required]],
      customHeaders: [''],
      queryParameters: [''],
      bodyPayload: [''],
      authenticationDetails: [''],
      authenticationType: ['', [Validators.required]],
      responseFormat: ['', [Validators.required]],
      dataPathInResponse: ['', [Validators.required]],
      expectedPullFrequency: ['', [Validators.required]],
      additionalNotes: ['']
    });

    // Update stepper step label based on metadata flow
    this.updateStepperSteps();
    
    // Initialize createdDatasetId from localStorage if it exists
    this.initializeCreatedItemId();
  }

  /**
   * Initialize createdDatasetId from localStorage if item was already created
   */
  private initializeCreatedItemId(): void {
    const assetIdFromQuery =
      this.route.snapshot.queryParamMap.get('assetId') ||
      this.route.snapshot.queryParamMap.get('datasetId') ||
      this.route.snapshot.queryParamMap.get('modelId');

    const path = (this.router.url || '').split('?')[0];

    if (
      assetIdFromQuery &&
      (path.includes('/add-dataset/uploads') || path.includes('/add-model/uploads'))
    ) {
      this.createdDatasetId = assetIdFromQuery;
      const key = this.getLocalStorageKey();
      try {
        const prev = JSON.parse(localStorage.getItem(key) || '{}');
        const next = {
          ...prev,
          itemId: assetIdFromQuery,
          id: assetIdFromQuery,
        };
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        localStorage.setItem(
          key,
          JSON.stringify({ itemId: assetIdFromQuery, id: assetIdFromQuery }),
        );
      }
      return;
    }

    const localStorageKey = this.getLocalStorageKey();
    const savedData = localStorage.getItem(localStorageKey);
    
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const itemId = parsed.itemId || parsed.id || parsed.item_id;
        if (itemId && !this.createdDatasetId) {
          this.createdDatasetId = itemId;
          console.log('Initialized createdDatasetId from localStorage:', itemId);
        }
      } catch (error) {
        console.error('Error parsing saved data for item ID:', error);
      }
    }
  }

  /**
   * Update stepper steps based on metadata flow (Enter Metadata vs Upload Metadata)
   */
  private updateStepperSteps(): void {
    const localStorageKey = this.getLocalStorageKey();
    const savedData = localStorage.getItem(localStorageKey);
    
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const metadataOption = parsed.metadataOption;
        
        // Update step 2 label based on metadata option
        if (metadataOption === 'upload') {
          this.stepperSteps[1].label = 'Upload metadata';
        } else {
          this.stepperSteps[1].label = 'Enter Metadata';
        }
      } catch (error) {
        console.error('Error parsing saved data for stepper update:', error);
      }
    }
  }


  selectUploadOption(option: string): void {
    this.selectedUploadOption = option;
    // Clear files when switching options
    if (option !== 'upload-file') {
      this.selectedFiles = [];
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  getFileSize(size: number): string {
    if (size < 1024) {
      return size + ' B';
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(2) + ' KB';
    } else {
      return (size / (1024 * 1024)).toFixed(2) + ' MB';
    }
  }

  isUploadFileSelected(): boolean {
    return this.selectedUploadOption === 'upload-file';
  }

  /**
   * Check if an upload option should be disabled
   * Disable SFTP/API/Direct Push if files are uploaded to S3
   */
  /** True when current flow is for model (not dataset). */
  private isModelUpload(): boolean {
    return this.router.url.includes('/add-model/');
  }

  isUploadOptionDisabled(optionValue: string): boolean {
    // For model upload: only Upload File is allowed; disable SFTP, API Integration, Direct Push
    if (this.isModelUpload()) {
      return optionValue === 'sftp-upload' || optionValue === 'api-integration' || optionValue === 'direct-push';
    }
    // If Upload File is selected and files are uploaded, disable other options
    if (this.isUploadFileSelected() && this.uploadedFiles.length > 0 && this.isAllCompleted()) {
      return optionValue !== 'upload-file';
    }
    return false;
  }

  /**
   * Check if Publish button should be disabled
   * Disable until files are uploaded (for Upload File option) or forms are valid (for SFTP/API)
   */
  isPublishButtonDisabled(): boolean {
    // Disable if no upload option is selected
    if (!this.selectedUploadOption || this.selectedUploadOption === '') {
      return true;
    }

    // For Upload File option, require files to be uploaded
    if (this.isUploadFileSelected()) {
      // Disable if no files uploaded or not all completed
      return this.uploadedFiles.length === 0 || !this.isAllCompleted();
    }

    // For SFTP option, require form to be valid
    if (this.isSftpUploadSelected()) {
      return !this.sftpForm || this.sftpForm.invalid;
    }

    // For API Integration option, require form to be valid
    if (this.isApiIntegrationSelected()) {
      return !this.apiIntegrationForm || this.apiIntegrationForm.invalid;
    }

    // For Direct Push, enable the button (no file upload required)
    // Direct Push just provides code/instructions, so it's always ready
    return false;
  }

  /**
   * Handle drag over event
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = true;
  }

  /**
   * Handle drag leave event
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;
  }

  /**
   * Handle drop event
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;

    if (event.dataTransfer?.files) {
      const files = Array.from(event.dataTransfer.files);
      this.addFiles(files);
    }
  }

  /**
   * Handle file selection from input
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files = Array.from(input.files);
      this.addFiles(files);
      input.value = ''; // Reset input
    }
  }

  /**
   * Add files to the selected files list. Only allows files matching step 3 format + CSV + TXT.
   */
  private addFiles(files: File[]): void {
    const allowed = this.getAllowedExtensions();
    let rejectedCount = 0;
    for (const file of files) {
      if (!this.isAllowedFile(file)) {
        rejectedCount++;
        continue;
      }
      const exists = this.selectedFiles.some(
        (f) => f.name === file.name && f.size === file.size
      );
      if (!exists) {
        this.selectedFiles.push(file);
      }
    }
    if (rejectedCount > 0) {
      this.toasterService.error(
        `Only ${this.getAllowedFileTypesLabel()} files can be uploaded. ${rejectedCount} file(s) were not added.`
      );
    }
  }

  isSftpUploadSelected(): boolean {
    return this.selectedUploadOption === 'sftp-upload';
  }

  isApiIntegrationSelected(): boolean {
    return this.selectedUploadOption === 'api-integration';
  }

  isDirectPushSelected(): boolean {
    return this.selectedUploadOption === 'direct-push';
  }

  // Direct Push Code Snippet
  directPushCode = `#!/usr/bin/env python3
"""
Databank File Uploader

A Python script to upload large files to the MahaAgX Files Connect API using multipart upload.
Supports both single file and directory uploads with proper authentication and progress tracking.

Usage:
    Configure the variables at the top of the script and run:
    python databank_uploader.py
"""

import os
import json
import requests
import hashlib
import time
import mimetypes
from typing import Dict, List, Optional, Tuple
from pathlib import Path
from dataclasses import dataclass
from urllib.parse import urljoin
import logging
from datetime import datetime, timedelta

# Configure logging
logging.basicConfig(
    level=logging.INFO,
    format='%(asctime)s - %(levelname)s - %(message)s'
)
logger = logging.getLogger(__name__)

# =============================================================================
# CONFIGURATION VARIABLES - UPDATE THESE
# =============================================================================

# API Configuration
API_BASE_URL = "https://your-api-domain.com"  # Update with your API base URL
API_VERSION = "v1"

# Authentication Configuration
KEYCLOAK_TOKEN_URL = "https://idp.tgdex.telangana.gov.in/auth/realms/tgdex/protocol/openid-connect/token"
USERNAME = "sunil.babu@datakaveri.org"
PASSWORD = "Sunil@4989"
CLIENT_ID = "angular-client"

# Upload Configuration
DATABANK_ID = "your-databank-id"  # Update with your databank ID

# File/Directory Configuration
# Set either FILE_PATH for single file or DIRECTORY_PATH for directory upload
FILE_PATH = None  # e.g., "/path/to/your/file.csv"
DIRECTORY_PATH = None  # e.g., "/path/to/your/directory"

# Upload Configuration
CHUNK_SIZE = 1024 * 1024  # 1MB chunks (matches API default)
MAX_FILE_SIZE_GB = 1000  # Maximum file size in GB (matches API default)
MAX_RETRIES = 3
RETRY_DELAY = 1  # seconds

# =============================================================================
# DATA CLASSES
# =============================================================================

@dataclass
class AuthToken:
    """Authentication token data"""
    access_token: str
    refresh_token: Optional[str]
    expires_in: int
    token_type: str = "Bearer"
    expires_at: Optional[datetime] = None
    
    def __post_init__(self):
        """Set expiration time after initialization"""
        if self.expires_at is None:
            self.expires_at = datetime.now() + timedelta(seconds=self.expires_in - 60)  # 60 seconds buffer
    
    @property
    def header_value(self) -> str:
        return f"{self.token_type} {self.access_token}"
    
    @property
    def is_expired(self) -> bool:
        """Check if token is expired or about to expire"""
        return datetime.now() >= self.expires_at

@dataclass
class UploadPart:
    """Upload part information"""
    part_number: int
    presigned_url: str
    etag: Optional[str] = None
    uploaded: bool = False

@dataclass
class UploadSession:
    """Upload session information"""
    upload_id: str
    key: str
    parts: List[UploadPart]
    total_parts: int

@dataclass
class FileInfo:
    """File information for upload"""
    path: Path
    size: int
    content_type: str
    relative_path: str  # Path relative to upload directory

# =============================================================================
# UTILITY FUNCTIONS
# =============================================================================

def get_content_type(file_path: Path) -> str:
    """Get content type for a file"""
    content_type, _ = mimetypes.guess_type(str(file_path))
    if content_type is None:
        # Default mappings for common file types
        ext = file_path.suffix.lower()
        content_type_map = {
            '.csv': 'text/csv',
            '.json': 'application/json',
            '.txt': 'text/plain',
            '.parquet': 'application/octet-stream',
            '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            '.zip': 'application/zip'
        }
        content_type = content_type_map.get(ext, 'application/octet-stream')
    return content_type

def calculate_file_parts(file_size: int, chunk_size: int) -> int:
    """Calculate number of parts needed for multipart upload"""
    return (file_size + chunk_size - 1) // chunk_size

def validate_file_size(file_size: int) -> bool:
    """Validate file size against maximum allowed"""
    max_size_bytes = MAX_FILE_SIZE_GB * 1024 * 1024 * 1024
    return file_size <= max_size_bytes

def get_file_info(file_path: Path, base_path: Path = None) -> FileInfo:
    """Get file information"""
    if not file_path.exists():
        raise FileNotFoundError(f"File not found: {file_path}")
    
    if not file_path.is_file():
        raise ValueError(f"Path is not a file: {file_path}")
    
    file_size = file_path.stat().st_size
    
    if not validate_file_size(file_size):
        raise ValueError(f"File size ({file_size / (1024**3):.2f} GB) exceeds maximum allowed ({MAX_FILE_SIZE_GB} GB)")
    
    content_type = get_content_type(file_path)
    
    if base_path:
        relative_path = str(file_path.relative_to(base_path))
    else:
        relative_path = file_path.name
    
    return FileInfo(
        path=file_path,
        size=file_size,
        content_type=content_type,
        relative_path=relative_path
    )

def collect_files_from_directory(directory_path: Path) -> List[FileInfo]:
    """Collect all supported files from directory"""
    if not directory_path.exists():
        raise FileNotFoundError(f"Directory not found: {directory_path}")
    
    if not directory_path.is_dir():
        raise ValueError(f"Path is not a directory: {directory_path}")
    
    files = []
    for file_path in directory_path.rglob('*'):
        if file_path.is_file():
            try:
                file_info = get_file_info(file_path, directory_path)
                files.append(file_info)
            except ValueError as e:
                logger.warning(f"Skipping file {file_path}: {e}")
    
    return files

# =============================================================================
# API CLIENT
# =============================================================================

class DatabankAPIClient:
    """Client for interacting with the Databank API"""
    
    def __init__(self, base_url: str, version: str = "v1"):
        self.base_url = base_url.rstrip('/')
        self.version = version
        self.session = requests.Session()
        self.token: Optional[AuthToken] = None
    
    def _get_api_url(self, endpoint: str) -> str:
        """Get full API URL for an endpoint"""
        return urljoin(f"{self.base_url}/{self.version}/", endpoint.lstrip('/'))
    
    def _make_request(self, method: str, endpoint: str, **kwargs) -> requests.Response:
        """Make authenticated API request"""
        url = self._get_api_url(endpoint)
        headers = kwargs.get('headers', {})
        
        # Check if token needs refresh before making request
        if self.token and self.token.is_expired:
            logger.info("Token expired, refreshing...")
            self._refresh_token()
        
        if self.token:
            headers['Authorization'] = self.token.header_value
        
        kwargs['headers'] = headers
        
        logger.debug(f"{method.upper()} {url}")
        
        for attempt in range(MAX_RETRIES):
            try:
                response = self.session.request(method, url, **kwargs)
                
                if response.status_code == 401:
                    logger.info("Token invalid, attempting refresh...")
                    self._refresh_token()
                    headers['Authorization'] = self.token.header_value
                    kwargs['headers'] = headers
                    response = self.session.request(method, url, **kwargs)
                
                return response
                
            except requests.exceptions.RequestException as e:
                logger.warning(f"Request failed (attempt {attempt + 1}/{MAX_RETRIES}): {e}")
                if attempt < MAX_RETRIES - 1:
                    time.sleep(RETRY_DELAY * (2 ** attempt))
                else:
                    raise
    
    def authenticate(self) -> AuthToken:
        """Authenticate and get access token"""
        logger.info("Authenticating with Keycloak...")
        
        data = {
            'grant_type': 'password',
            'client_id': CLIENT_ID,
            'username': USERNAME,
            'password': PASSWORD
        }
        
        response = requests.post(
            KEYCLOAK_TOKEN_URL,
            data=data,
            headers={'Content-Type': 'application/x-www-form-urlencoded'}
        )
        
        if response.status_code != 200:
            raise Exception(f"Authentication failed: {response.status_code} - {response.text}")
        
        token_data = response.json()
        self.token = AuthToken(
            access_token=token_data['access_token'],
            refresh_token=token_data.get('refresh_token'),
            expires_in=token_data['expires_in'],
            token_type=token_data.get('token_type', 'Bearer')
        )
        
        logger.info("Authentication successful")
        return self.token
    
    def _refresh_token(self) -> AuthToken:
        """Refresh access token using refresh token"""
        if not self.token or not self.token.refresh_token:
            logger.info("No refresh token available, re-authenticating...")
            return self.authenticate()
        
        logger.info("Refreshing access token...")
        
        data = {
            'grant_type': 'refresh_token',
            'client_id': CLIENT_ID,
            'refresh_token': self.token.refresh_token
        }
        
        response = requests.post(
            KEYCLOAK_TOKEN_URL,
            data=data,
            headers={'Content-Type': 'application/x-www-form-urlencoded'}
        )
        
        if response.status_code != 200:
            logger.warning(f"Token refresh failed: {response.status_code} - {response.text}")
            logger.info("Falling back to full authentication...")
            return self.authenticate()
        
        token_data = response.json()
        self.token = AuthToken(
            access_token=token_data['access_token'],
            refresh_token=token_data.get('refresh_token', self.token.refresh_token),
            expires_in=token_data['expires_in'],
            token_type=token_data.get('token_type', 'Bearer')
        )
        
        logger.info("Token refresh successful")
        return self.token
    
    def initiate_upload(self, key: str, num_parts: int, content_type: str, databank_id: str) -> UploadSession:
        """Initiate multipart upload"""
        logger.info(f"Initiating upload: {key} ({num_parts} parts)")
        
        payload = {
            'key': key,
            'numParts': num_parts,
            'contentType': content_type
        }
        
        response = self._make_request(
            'POST',
            f'databanks/{databank_id}/uploads',
            json=payload
        )
        
        if response.status_code != 200:
            raise Exception(f"Failed to initiate upload: {response.status_code} - {response.text}")
        
        data = response.json()
        if not data.get('success', False):
            raise Exception(f"Upload initiation failed: {data.get('error', 'Unknown error')}")
        
        result = data['result']
        parts = [
            UploadPart(part_number=part['partNumber'], presigned_url=part['presignedUrl'])
            for part in result['parts']
        ]
        
        return UploadSession(
            upload_id=result['uploadId'],
            key=result['key'],
            parts=parts,
            total_parts=len(parts)
        )
    
    def upload_part(self, part: UploadPart, data: bytes) -> str:
        """Upload a single part"""
        logger.debug(f"Uploading part {part.part_number}")
        
        response = requests.put(
            part.presigned_url,
            data=data,
            headers={'Content-Type': 'application/octet-stream'}
        )
        
        if response.status_code != 200:
            raise Exception(f"Failed to upload part {part.part_number}: {response.status_code} - {response.text}")
        
        etag = response.headers.get('ETag', '').strip('"')
        part.etag = etag
        part.uploaded = True
        
        return etag
    
    def complete_upload(self, session: UploadSession, databank_id: str) -> Dict:
        """Complete multipart upload"""
        logger.info(f"Completing upload: {session.key}")
        
        parts = [
            {
                'PartNumber': part.part_number,
                'ETag': part.etag
            }
            for part in session.parts if part.uploaded
        ]
        
        payload = {
            'key': session.key,
            'parts': parts
        }
        
        response = self._make_request(
            'PUT',
            f'databanks/{databank_id}/uploads/{session.upload_id}',
            json=payload
        )
        
        if response.status_code != 200:
            raise Exception(f"Failed to complete upload: {response.status_code} - {response.text}")
        
        data = response.json()
        if not data.get('success', False):
            raise Exception(f"Upload completion failed: {data.get('error', 'Unknown error')}")
        
        return data['result']
    
    def cancel_upload(self, session: UploadSession, databank_id: str) -> None:
        """Cancel multipart upload"""
        logger.info(f"Canceling upload: {session.key}")
        
        payload = {
            'key': session.key
        }
        
        response = self._make_request(
            'POST',
            f'databanks/{databank_id}/uploads/{session.upload_id}/cancel',
            json=payload
        )
        
        if response.status_code != 200:
            logger.warning(f"Failed to cancel upload: {response.status_code} - {response.text}")

# =============================================================================
# FILE UPLOADER
# =============================================================================

class FileUploader:
    """Handles file upload operations"""
    
    def __init__(self, client: DatabankAPIClient, databank_id: str):
        self.client = client
        self.databank_id = databank_id
    
    def upload_file(self, file_info: FileInfo) -> Dict:
        """Upload a single file"""
        logger.info(f"Starting upload: {file_info.relative_path} ({file_info.size} bytes)")
        
        # Calculate number of parts
        num_parts = calculate_file_parts(file_info.size, CHUNK_SIZE)
        
        # Initiate upload
        session = self.client.initiate_upload(
            key=file_info.relative_path,
            num_parts=num_parts,
            content_type=file_info.content_type,
            databank_id=self.databank_id
        )
        
        try:
            # Upload parts
            with open(file_info.path, 'rb') as f:
                for i, part in enumerate(session.parts):
                    # Read chunk
                    chunk = f.read(CHUNK_SIZE)
                    if not chunk:
                        break
                    
                    # Upload part
                    self.client.upload_part(part, chunk)
                    
                    # Progress
                    progress = (i + 1) / session.total_parts * 100
                    logger.info(f"Progress: {progress:.1f}% ({i + 1}/{session.total_parts} parts)")
            
            # Complete upload
            result = self.client.complete_upload(session, self.databank_id)
            
            logger.info(f"Upload completed successfully: {file_info.relative_path}")
            return result
            
        except Exception as e:
            logger.error(f"Upload failed: {e}")
            # Cancel upload on failure
            try:
                self.client.cancel_upload(session, self.databank_id)
            except Exception as cancel_error:
                logger.warning(f"Failed to cancel upload: {cancel_error}")
            raise
    
    def upload_files(self, files: List[FileInfo]) -> List[Dict]:
        """Upload multiple files"""
        results = []
        
        for i, file_info in enumerate(files):
            logger.info(f"Uploading file {i + 1}/{len(files)}: {file_info.relative_path}")
            
            try:
                result = self.upload_file(file_info)
                results.append({
                    'file': file_info.relative_path,
                    'success': True,
                    'result': result
                })
            except Exception as e:
                logger.error(f"Failed to upload {file_info.relative_path}: {e}")
                results.append({
                    'file': file_info.relative_path,
                    'success': False,
                    'error': str(e)
                })
        
        return results

# =============================================================================
# MAIN EXECUTION
# =============================================================================

def main():
    """Main execution function"""
    # Validate configuration
    if not API_BASE_URL or API_BASE_URL == "https://your-api-domain.com":
        raise ValueError("Please configure API_BASE_URL")
    
    if not DATABANK_ID or DATABANK_ID == "your-databank-id":
        raise ValueError("Please configure DATABANK_ID")
    
    if not FILE_PATH and not DIRECTORY_PATH:
        raise ValueError("Please configure either FILE_PATH or DIRECTORY_PATH")
    
    if FILE_PATH and DIRECTORY_PATH:
        raise ValueError("Configure either FILE_PATH or DIRECTORY_PATH, not both")
    
    # Initialize client
    client = DatabankAPIClient(API_BASE_URL, API_VERSION)
    
    # Authenticate
    client.authenticate()
    
    # Initialize uploader
    uploader = FileUploader(client, DATABANK_ID)
    
    # Collect files
    files = []
    if FILE_PATH:
        file_path = Path(FILE_PATH)
        file_info = get_file_info(file_path)
        files.append(file_info)
        logger.info(f"Single file upload: {file_info.relative_path}")
    else:
        directory_path = Path(DIRECTORY_PATH)
        files = collect_files_from_directory(directory_path)
        logger.info(f"Directory upload: {len(files)} files found")
    
    if not files:
        logger.warning("No files to upload")
        return
    
    # Upload files
    logger.info(f"Starting upload of {len(files)} files to databank {DATABANK_ID}")
    results = uploader.upload_files(files)
    
    # Summary
    successful = sum(1 for r in results if r['success'])
    failed = len(results) - successful
    
    logger.info(f"Upload completed: {successful} successful, {failed} failed")
    
    if failed > 0:
        logger.info("Failed uploads:")
        for result in results:
            if not result['success']:
                logger.info(f"  - {result['file']}: {result['error']}")

if __name__ == "__main__":
    try:
        main()
    except Exception as e:
        logger.error(f"Script failed: {e}")
        exit(1)`;

  copyCodeToClipboard(): void {
    navigator.clipboard.writeText(this.directPushCode).then(() => {
      this.toasterService.success('Code copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy code:', err);
      this.toasterService.error('Failed to copy code to clipboard');
    });
  }

  downloadCode(): void {
    const blob = new Blob([this.directPushCode], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'databank_uploader.py';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    this.toasterService.success('Code downloaded successfully!');
  }

  onSubmitDirectPush(): void {
    // Show success modal for Direct Push submission
    this.isUploadSuccessModalOpen = true;
  }

  async onSubmitSftpForm(): Promise<void> {
    if (this.sftpForm.valid) {
      // Call the same logic as onNext() for SFTP upload
      await this.onNext();
    } else {
      this.sftpForm.markAllAsTouched();
      this.toasterService.error('Please fill all required SFTP fields.');
    }
  }

  async onSubmitApiIntegrationForm(): Promise<void> {
    if (this.apiIntegrationForm.valid) {
      // Reuse stepper Publish logic for API Integration
      await this.onNext();
    } else {
      this.apiIntegrationForm.markAllAsTouched();
      this.toasterService.error('Please fill all required API Integration fields.');
    }
  }

  clearAllFiles(): void {
    this.selectedFiles = [];
    this.uploadedFiles = [];
  }

  /**
   * Upload more files - keeps existing uploaded files and allows adding new ones
   */
  uploadMoreFiles(): void {
    // Clear only the selected files, keep uploaded files
    this.selectedFiles = [];
    // Trigger file input click to select more files
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  /**
   * Upload a single file from selected files
   */
  async uploadSingleFileFromSelected(index: number): Promise<void> {
    if (this.uploading || index < 0 || index >= this.selectedFiles.length) {
      return;
    }

    const file = this.selectedFiles[index];

    // Create uploadedFile entry for this single file
    const fileItem: typeof this.uploadedFiles[0] = {
      file,
      progress: 0,
      status: 'pending'
    };

    // Add to uploadedFiles if not already there
    if (!this.uploadedFiles.find(f => f.file === file)) {
      this.uploadedFiles.push(fileItem);
    }

    // Remove from selectedFiles
    this.selectedFiles.splice(index, 1);

    // Upload the file
    this.uploading = true;
    await this.uploadSingleFile(fileItem);
    this.uploading = false;

    if (fileItem.status === 'completed') {
      this.toasterService.success('File uploaded successfully!');
    }
  }

  /**
   * Upload all files
   */
  async uploadAllFiles(): Promise<void> {
    if (this.uploading || this.selectedFiles.length === 0) {
      return;
    }

    console.log('uploadAllFiles called with selectedFiles:', this.selectedFiles.length);

    // Convert selectedFiles to uploadedFiles format
    // Add newly selected files to uploadedFiles array
    for (const file of this.selectedFiles) {
      // Check if file is not already in uploadedFiles
      const exists = this.uploadedFiles.some(f => f.file.name === file.name && f.file.size === file.size);
      if (!exists) {
        this.uploadedFiles.push({
          file,
          progress: 0,
          status: 'pending' as const
        });
      }
    }

    // Clear selectedFiles after moving to uploadedFiles
    this.selectedFiles = [];

    console.log('uploadedFiles after conversion:', this.uploadedFiles.length);

    this.uploading = true;
    const pendingFiles = this.uploadedFiles.filter(f => f.status === 'pending' || f.status === 'error');

    console.log('Pending files to upload:', pendingFiles.length);

    for (const fileItem of pendingFiles) {
      await this.uploadSingleFile(fileItem);
    }

    this.uploading = false;

    // Check if all files uploaded successfully
    const allSuccess = this.uploadedFiles.every(f => f.status === 'completed');
    if (allSuccess) {
      this.toasterService.success('All files uploaded successfully!');
    }
  }

  /**
   * Upload a single file using multipart upload (5-step process)
   */
  private async uploadSingleFile(fileItem: typeof this.uploadedFiles[0]): Promise<void> {
    try {
      fileItem.status = 'uploading';
      fileItem.progress = 0;
      fileItem.error = undefined;

      // Get databankId from localStorage
      const localStorageKey = this.getLocalStorageKey();
      const existingData = localStorage.getItem(localStorageKey);
      const formData = existingData ? JSON.parse(existingData) : {};
      const databankId = formData.itemId || formData.id || formData.item_id;

      if (!databankId) {
        throw new Error('Databank ID not found. Please complete previous steps first.');
      }

      const file = fileItem.file;
      console.log('Starting 5-step upload process for:', file.name);

      // ============================================
      // STEP 1: Initiate Upload
      // ============================================
      console.log('Step 1: Initiating upload...');
      const initiateResponse = await this.fileApiService.initiateUpload(
        databankId,
        file.name,
        file.type || 'application/octet-stream',
        1 // Single part upload
      );

      console.log('Step 1 Complete - Upload initiated:', initiateResponse);
      console.log('Response data structure:', JSON.stringify(initiateResponse.data, null, 2));

      // Handle nested response structure: { success: true, data: { uploadId, key, parts } }
      const responseData = initiateResponse.data.data || initiateResponse.data;
      const uploadId = responseData.uploadId;
      
      // The response structure has parts array with presignedUrl
      const presignedUrls = responseData.parts?.map((part: any) => part.presignedUrl) || [];

      console.log('Extracted uploadId:', uploadId);
      console.log('Extracted presigned URLs:', presignedUrls);

      if (!presignedUrls || presignedUrls.length === 0) {
        console.error('Full response:', initiateResponse);
        throw new Error('No presigned URLs received from server');
      }

      fileItem.progress = 10;

      // ============================================
      // STEP 2: Upload to S3/MinIO (Presigned URL)
      // ============================================
      console.log('Step 2: Uploading file to S3/MinIO...');
      const uploadResponse = await this.fileApiService.uploadPart(
        presignedUrls[0],
        file
      );

      console.log('Step 2 Complete - File uploaded to S3:', uploadResponse);

      const etag = uploadResponse.headers.get('ETag')?.replace(/"/g, '') || '';
      
      if (!etag) {
        throw new Error('No ETag received from S3 upload');
      }

      fileItem.progress = 40;

      // ============================================
      // STEP 3: Complete Upload
      // ============================================
      console.log('Step 3: Completing upload...');
      const completeResponse = await this.fileApiService.completeUpload(
        databankId,
        uploadId,
        file.name,
        [{ partNumber: 1, eTag: etag }]
      );

      console.log('Step 3 Complete - Upload completed:', completeResponse);
      fileItem.progress = 60;

      // ============================================
      // STEP 4: Refresh File List
      // ============================================
      console.log('Step 4: Refreshing file list...');
      const fileListResponse = await this.fileApiService.listFiles(
        databankId,
        1000,
        true
      );

      console.log('Step 4 Complete - File list refreshed:', fileListResponse);
      fileItem.progress = 80;

      // ============================================
      // STEP 5: Generate File Preview (non-blocking)
      // ============================================
      console.log('Step 5: Generating file preview...');
      try {
        const previewResponse = await this.fileApiService.generatePreview(
          databankId,
          file.name
        );
        console.log('Step 5 Complete - Preview generated:', previewResponse);
        fileItem.previewData = previewResponse.data;
      } catch (previewError) {
        console.warn('Step 5: Preview generation failed (non-blocking):', previewError);
      }

      fileItem.uploadId = uploadId;
      fileItem.key = file.name;
      fileItem.progress = 100;
      fileItem.status = 'completed';

      console.log('✅ All 5 steps completed successfully for:', file.name);
    } catch (error: any) {
      fileItem.status = 'error';
      fileItem.error = this.getUserFriendlyErrorMessage(error);
      console.error('Upload error:', error);
    }
  }

  /**
   * Get user-friendly error message
   */
  private getUserFriendlyErrorMessage(error: any): string {
    const errorMessage = error?.message || error?.toString() || '';

    if (errorMessage.includes('0 Unknown Error') || errorMessage.includes('ERR_NETWORK')) {
      return 'Upload failed: Please check your internet connection and try again.';
    }
    if (errorMessage.includes('404')) {
      return 'Upload failed: Service not found. Please contact support.';
    }
    if (errorMessage.includes('500') || errorMessage.includes('Internal Server Error')) {
      return 'Upload failed: Server error. Please try again later.';
    }
    if (errorMessage.includes('413') || errorMessage.includes('Payload Too Large')) {
      return 'Upload failed: File is too large. Please choose a smaller file.';
    }
    if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
      return 'Upload failed: You do not have permission to upload this file.';
    }
    if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
      return 'Upload failed: Please log in again and try.';
    }
    if (errorMessage.includes('timeout') || errorMessage.includes('TimeoutError')) {
      return 'Upload failed: Request timed out. Please try again.';
    }

    return 'Upload failed: Something went wrong. Please try again.';
  }

  closeUploadSuccessModal(): void {
    this.isUploadSuccessModalOpen = false;
    this.requestType = null;
    // Optionally clear files after successful upload
    // this.selectedFiles = [];
  }

  /**
   * Close Direct Push success modal
   */
  closeDirectPushSuccessModal(): void {
    this.isDirectPushSuccessModalOpen = false;
  }

  /**
   * Handle "View Model/Dataset" button click from Direct Push modal
   */
  onViewDatasetFromDirectPush(): void {
    this.closeDirectPushSuccessModal();
    
    if (this.createdDatasetId) {
      // Navigate to detail page based on type
      if (this.isModel()) {
        this.router.navigate(['/model', this.createdDatasetId]);
      } else {
        this.router.navigate(['/dataset', this.createdDatasetId]);
      }
    } else {
      console.warn('Item ID not available for navigation');
      // Fallback: navigate to list page based on type
      if (this.isModel()) {
        this.router.navigate(['/models']);
      } else {
        this.router.navigate(['/datasets']);
      }
    }
  }

  /**
   * Handle "Create New" button click from Direct Push modal
   */
  onCreateNewFromDirectPush(): void {
    this.closeDirectPushSuccessModal();

    // Clear all flow state so Step 1 opens completely fresh
    const localStorageKey = this.getLocalStorageKey();
    localStorage.removeItem(localStorageKey);
    sessionStorage.removeItem('contributionFlowActive');
    sessionStorage.removeItem('cameFromUploadsStep');
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && (k.startsWith('updateBaseline:datasetFormData') || k.startsWith('updateBaseline:modelFormData'))) {
        sessionStorage.removeItem(k);
      }
    }

    this.createdDatasetId = null;

    const currentUrl = this.router.url;
    let navigateRoute: string;

    if (currentUrl.includes('/org-admin/')) {
      navigateRoute = currentUrl.includes('/add-model/') ? '/org-admin/add-model' : '/org-admin/add-dataset';
    } else {
      navigateRoute = currentUrl.includes('/add-model/')
        ? '/provider/add-contributions/add-model'
        : '/provider/add-contributions/add-dataset';
    }

    this.router.navigateByUrl(navigateRoute, { replaceUrl: true });
  }

  /**
   * Get the request type name for display
   */
  get requestTypeName(): string {
    if (this.requestType === 'SFTP') {
      return 'SFTP request';
    } else if (this.requestType === 'API') {
      return 'API integration';
    }
    return 'request';
  }

  /**
   * SFTP/API success modal title. Providers need org admin + platform admin;
   * org admins only need platform admin.
   */
  getUploadRequestSuccessTitle(): string {
    return this.isOrgAdminRoute
      ? 'Request sent to Platform Admin'
      : 'Request sent for Approval';
  }

  /**
   * SFTP/API success modal body. Matches publish success messaging for dual approval.
   */
  getUploadRequestSuccessMessage(): string {
    if (this.isOrgAdminRoute) {
      return (
        `Your ${this.requestTypeName} has been successfully submitted to the Platform Administrator.` +
        `<br><br>Your request is awaiting approval from the Platform Admin.`
      );
    }
    return (
      `Your ${this.requestTypeName} has been successfully submitted to the Platform Administrator.` +
      `<br><br>Your request is awaiting Organisation Admin approval for metadata and Platform Admin approval.`
    );
  }

  /**
   * Check if current flow is for AI Model
   */
  isModel(): boolean {
    return this.router.url.includes('/add-model/');
  }

  /**
   * Get success title based on type (model or dataset) and role (provider vs org-admin).
   * Provider-created assets require Organisation Admin approval before becoming available.
   */
  getSuccessTitle(): string {
    return this.isModel() ? 'Model Submitted for Approval' : 'Dataset Submitted for Approval';
  }

  /**
   * Get success message based on type (model or dataset) and role (provider vs org-admin).
   */
  getSuccessMessage(): string {
    if (this.isOrgAdminRoute) {
      return this.isModel()
        ? 'Your model request has been submitted and is awaiting platform admin approval.'
        : 'Your dataset request has been submitted and is awaiting platform admin approval.';
    }
    return this.isModel()
      ? 'Your model request has been submitted and is awaiting Organisation Admin approval for metadata and platform admin approval.'
      : 'Your dataset request has been submitted and is awaiting Organisation Admin approval for metadata and platform admin approval.';
  }

  /**
   * Get view button label based on type (model or dataset)
   */
  getViewButtonLabel(): string {
    return this.isModel() ? 'View Model' : 'View Dataset';
  }

  /**
   * Get the next steps message for the confirmation dialog
   */
  getNextStepsMessage(): string {
    const orgAdminMetadataStep = this.isOrgAdminRoute
      ? ''
      : 'Your Organisation Admin will review the metadata. ';
    if (this.requestType === 'SFTP') {
      return (
        orgAdminMetadataStep +
        'Our team will review your SFTP configuration details. You\'ll receive an email notification once your SFTP access is approved and set up.'
      );
    } else if (this.requestType === 'API') {
      return (
        orgAdminMetadataStep +
        'Our team will review your API integration details. You\'ll receive an email notification once your API integration is approved and set up.'
      );
    }
    return orgAdminMetadataStep.trim();
  }

  /**
   * Handle "View Dataset/Model" button click from request success modal
   */
  onViewDatasetFromRequest(): void {
    this.closeUploadSuccessModal();
    
    if (this.createdDatasetId) {
      // Navigate to detail page based on type
      if (this.isModel()) {
        // Navigate to model detail page (adjust route as needed)
        this.router.navigate(['/model', this.createdDatasetId]);
      } else {
        // Navigate to dataset detail page
        this.router.navigate(['/dataset', this.createdDatasetId]);
      }
    } else {
      console.warn('Item ID not available for navigation');
      // Fallback: navigate to list page based on type
      if (this.isModel()) {
        this.router.navigate(['/models']);
      } else {
        this.router.navigate(['/datasets']);
      }
    }
  }

  /**
   * Handle "Create New" button click from request success modal
   */
  onCreateNewFromRequest(): void {
    this.closeUploadSuccessModal();

    // Clear all flow state so Step 1 opens completely fresh
    const localStorageKey = this.getLocalStorageKey();
    localStorage.removeItem(localStorageKey);
    sessionStorage.removeItem('contributionFlowActive');
    sessionStorage.removeItem('cameFromUploadsStep');
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && (k.startsWith('updateBaseline:datasetFormData') || k.startsWith('updateBaseline:modelFormData'))) {
        sessionStorage.removeItem(k);
      }
    }

    this.createdDatasetId = null;
    this.requestType = null;

    const currentUrl = this.router.url;
    let navigateRoute: string;

    if (currentUrl.includes('/org-admin/')) {
      navigateRoute = currentUrl.includes('/add-model/') ? '/org-admin/add-model' : '/org-admin/add-dataset';
    } else {
      navigateRoute = currentUrl.includes('/add-model/')
        ? '/provider/add-contributions/add-model'
        : '/provider/add-contributions/add-dataset';
    }

    this.router.navigateByUrl(navigateRoute, { replaceUrl: true });
  }

  /** Returns true for file types we can render directly via FileReader. */
  isDirectlyPreviewable(file: File): boolean {
    const ext = this.getFileExtension(file.name).toLowerCase();
    return ext === '.csv' || ext === '.txt';
  }

  /**
   * Open file preview modal.
   * CSV and TXT files are read directly from the File object via FileReader.
   * All other types fall back to the server-generated preview data.
   */
  openFilePreview(fileItem: typeof this.uploadedFiles[0]): void {
    const file = fileItem.file;
    const ext = this.getFileExtension(file.name).toLowerCase();

    if (ext === '.csv' || ext === '.txt') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = (e.target?.result as string) ?? '';
        this.previewingFile = {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          previewData: fileItem.previewData,
          parsedData: ext === '.csv' ? this.parseCSV(content) : null,
          rawText: ext === '.txt' ? content : null,
        };
        this.previewModalOpen = true;
        this.cdr.detectChanges();
      };
      reader.onerror = () => {
        this.toasterService.error('Failed to read file for preview');
      };
      reader.readAsText(file);
      return;
    }

    if (fileItem.previewData) {
      this.previewingFile = {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        previewData: fileItem.previewData,
        parsedData: this.parsePreviewData(fileItem.previewData, file.name),
        rawText: null,
      };
      this.previewModalOpen = true;
    } else {
      this.toasterService.error('Preview not available for this file');
    }
  }

  /**
   * Parse preview data into table format
   */
  parsePreviewData(previewData: any, fileName: string): { headers: string[], rows: any[][] } | null {
    try {
      if (!previewData) return null;

      // Top-level string → CSV
      if (typeof previewData === 'string') {
        return this.parseCSV(previewData);
      }

      // { success: true, data: "csv string" } or { data: "csv string" }
      if (typeof previewData.data === 'string' && previewData.data.trim().length > 0) {
        return this.parseCSV(previewData.data);
      }

      // { data: { content: "csv string" } } or { success: true, data: { content: "csv string" } }
      if (typeof previewData.data?.content === 'string' && previewData.data.content.trim().length > 0) {
        return this.parseCSV(previewData.data.content);
      }

      // { content: "csv string" }
      if (typeof previewData.content === 'string' && previewData.content.trim().length > 0) {
        return this.parseCSV(previewData.content);
      }

      // { success: true, data: { content: { data: [...] } } }
      if (previewData.data?.content?.data) {
        const data = previewData.data.content.data;
        if (Array.isArray(data) && data.length > 0) {
          const headers = Object.keys(data[0]);
          const rows = data.map((row: any) => Object.values(row));
          return { headers, rows };
        }
      }

      // { content: { data: [...] } }
      if (previewData.content?.data) {
        const data = previewData.content.data;
        if (Array.isArray(data) && data.length > 0) {
          const headers = Object.keys(data[0]);
          const rows = data.map((row: any) => Object.values(row));
          return { headers, rows };
        }
      }

      // { data: [...] } — array of objects or array of arrays
      if (previewData.data && Array.isArray(previewData.data)) {
        const data = previewData.data;
        if (data.length === 0) return null;
        const headers = typeof data[0] === 'object' && !Array.isArray(data[0])
          ? Object.keys(data[0])
          : data[0];
        const rows = typeof data[0] === 'object' && !Array.isArray(data[0])
          ? data.map((row: any) => Object.values(row))
          : data.slice(1);
        return { headers, rows };
      }

      // Top-level array of objects
      if (Array.isArray(previewData) && previewData.length > 0) {
        const headers = Object.keys(previewData[0]);
        const rows = previewData.map((row: any) => Object.values(row));
        return { headers, rows };
      }

      return null;
    } catch (error) {
      console.error('Error parsing preview data:', error);
      return null;
    }
  }

  /**
   * Parse CSV string into table format
   */
  parseCSV(csvString: string): { headers: string[], rows: any[][] } {
    const lines = csvString.trim().split('\n');
    if (lines.length === 0) return { headers: [], rows: [] };
    
    // Parse CSV line considering quoted values
    const parseLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };
    
    const headers = parseLine(lines[0]);
    const rows = lines.slice(1).map(line => parseLine(line));
    
    return { headers, rows };
  }

  /**
   * Extract a human-readable reason from the preview API response when preview is unsupported.
   * Returns an empty string when no specific reason is available.
   */
  getPreviewUnavailableReason(previewData: any): string {
    if (!previewData) return '';
    const msg =
      previewData?.data?.content?.message ||
      previewData?.content?.message ||
      previewData?.data?.message ||
      previewData?.message ||
      '';
    return typeof msg === 'string' ? msg : '';
  }

  /**
   * Close file preview modal
   */
  closePreview(): void {
    this.previewModalOpen = false;
    this.previewingFile = null;
  }

  /**
   * Check if all uploaded files are completed
   */
  isAllCompleted(): boolean {
    return this.uploadedFiles.length > 0 && 
           this.uploadedFiles.every(f => f.status === 'completed');
  }

  // Dropdown methods for API Integration
  toggleHttpMethodDropdown(): void {
    this.isHttpMethodDropdownOpen = !this.isHttpMethodDropdownOpen;
    this.isAuthenticationTypeDropdownOpen = false;
    this.isResponseFormatDropdownOpen = false;
  }

  selectHttpMethod(method: string): void {
    this.apiIntegrationForm.patchValue({ httpMethod: method });
    this.isHttpMethodDropdownOpen = false;
    this.apiIntegrationForm.get('httpMethod')?.markAsTouched();
  }

  getSelectedHttpMethod(): string {
    return this.apiIntegrationForm.get('httpMethod')?.value || 'Select HTTP Method';
  }

  isHttpMethodSelected(method: string): boolean {
    return this.apiIntegrationForm.get('httpMethod')?.value === method;
  }

  toggleAuthenticationTypeDropdown(): void {
    this.isAuthenticationTypeDropdownOpen = !this.isAuthenticationTypeDropdownOpen;
    this.isHttpMethodDropdownOpen = false;
    this.isResponseFormatDropdownOpen = false;
  }

  selectAuthenticationType(type: string): void {
    this.apiIntegrationForm.patchValue({ authenticationType: type });
    this.isAuthenticationTypeDropdownOpen = false;
    this.apiIntegrationForm.get('authenticationType')?.markAsTouched();
  }

  getSelectedAuthenticationType(): string {
    return this.apiIntegrationForm.get('authenticationType')?.value || 'Select Authentication Type';
  }

  isAuthenticationTypeSelected(type: string): boolean {
    return this.apiIntegrationForm.get('authenticationType')?.value === type;
  }

  toggleResponseFormatDropdown(): void {
    this.isResponseFormatDropdownOpen = !this.isResponseFormatDropdownOpen;
    this.isHttpMethodDropdownOpen = false;
    this.isAuthenticationTypeDropdownOpen = false;
  }

  selectResponseFormat(format: string): void {
    this.apiIntegrationForm.patchValue({ responseFormat: format });
    this.isResponseFormatDropdownOpen = false;
    this.apiIntegrationForm.get('responseFormat')?.markAsTouched();
  }

  getSelectedResponseFormat(): string {
    return this.apiIntegrationForm.get('responseFormat')?.value || 'Select Response Format';
  }

  isResponseFormatSelected(format: string): boolean {
    return this.apiIntegrationForm.get('responseFormat')?.value === format;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target?.closest?.('.custom-dropdown-wrapper')) return;
    this.isHttpMethodDropdownOpen = false;
    this.isAuthenticationTypeDropdownOpen = false;
    this.isResponseFormatDropdownOpen = false;
  }

  goBack(): void {
    const localStorageKey = this.getLocalStorageKey();
    
    // Check if coming from upload-metadata flow by checking localStorage
    const savedData = localStorage.getItem(localStorageKey);
    let isUploadMetadataFlow = false;
    
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        isUploadMetadataFlow = data.metadataOption === 'upload';
      } catch (e) {
        // Ignore parsing errors
      }
    }
    
    // Navigate to previous step based on flow
    if (isUploadMetadataFlow) {
      this.router.navigate(['../preview-metadata'], { relativeTo: this.route });
    } else {
      // Set flag in sessionStorage to indicate we're coming from step 4 (uploads)
      // This will be picked up by additionalInfo component on initialization
      sessionStorage.setItem('cameFromUploadsStep', 'true');
      console.log('[Upload goBack] Setting cameFromUploadsStep flag in sessionStorage');
      this.router.navigate(['../additionalInfo'], { relativeTo: this.route });
    }
  }

  ngAfterViewInit(): void {
    // Update step 4 with template reference after view is initialized
    if (this.step4Template) {
      this.stepperSteps[3].content = this.step4Template;
      this.cdr.detectChanges();
    }
  }

  onStepChange(stepIndex: number): void {
    // Determine route base from current URL
    const currentUrl = this.router.url;
    let routeBase: string;
    
    if (currentUrl.includes('/add-dataset/')) {
      routeBase = 'provider/add-contributions/add-dataset';
    } else if (currentUrl.includes('/add-model/')) {
      routeBase = 'provider/add-contributions/add-model';
    } else {
      // Default to add-dataset if we can't determine from URL
      routeBase = 'provider/add-contributions/add-dataset';
    }
    
    const localStorageKey = this.getLocalStorageKey();
    
    // Check if coming from upload-metadata flow by checking localStorage
    const savedData = localStorage.getItem(localStorageKey);
    let isUploadMetadataFlow = false;
    
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        isUploadMetadataFlow = data.metadataOption === 'upload';
      } catch (e) {
        // Ignore parsing errors
      }
    }
    
    // Navigate to the appropriate route based on step index and flow
    let routes: string[];
    if (isUploadMetadataFlow) {
      routes = [
        routeBase,
        `${routeBase}/upload-metadata`,
        `${routeBase}/preview-metadata`,
        `${routeBase}/uploads`
      ];
    } else {
      routes = [
        routeBase,
        `${routeBase}/metadata`,
        `${routeBase}/additionalInfo`,
        `${routeBase}/uploads`
      ];
    }
    
    if (stepIndex >= 0 && stepIndex < routes.length) {
      this.router.navigate([routes[stepIndex]]);
    }
  }

  async onNext(): Promise<void> {
    console.log('onNext() called in upload component - Publish button clicked');
    
    // Check if already creating to prevent duplicate calls
    if (this.isCreatingItem) {
      console.warn('Already creating item, skipping duplicate call');
      return;
    }

    // Get form data to determine update flow before try block
    const localStorageKey = this.getLocalStorageKey();
    const existingData = localStorage.getItem(localStorageKey);
    const formData = existingData ? JSON.parse(existingData) : {};
    let itemId: string | null = formData.itemId || formData.id || formData.item_id || null;
    const isModel = this.router.url.includes('/add-model/');

    try {
      console.log('Starting upload-step publish...');
      this.isCreatingItem = true;

      console.log('Form data from localStorage:', formData);
      console.log('Item ID:', itemId);

      // Ensure we have an itemId (if user landed on uploads without creating item earlier)
      if (!itemId) {
        console.log('No itemId found; creating item before upload request...');
        const itemPayload = await this.buildCreateAssetPayload(formData);
        const response = await this.catalogueApiService.createAssetItem(itemPayload);
        const createdItemId =
          response?.result?.itemId ||
          response?.result?.id ||
          response?.data?.id ||
          response?.id ||
          response?.result?.data?.id;

        if (createdItemId) {
          itemId = createdItemId;
          console.log('Storing item ID in localStorage:', createdItemId);
          formData.itemId = createdItemId;
          formData.id = createdItemId;
          localStorage.setItem(localStorageKey, JSON.stringify(formData));
          // Store the created item ID for navigation (for both datasets and models)
          this.createdDatasetId = createdItemId;
        } else {
          console.warn('Item ID not found in create response:', response);
        }
      }

      // If SFTP upload selected, save SFTP config via createDataUploadRequest
      if (this.isSftpUploadSelected()) {
        if (this.sftpForm.invalid) {
          Object.keys(this.sftpForm.controls).forEach(key => {
            this.sftpForm.get(key)?.markAsTouched();
          });
          this.toasterService.error('Please fill all required SFTP fields.');
          return;
        }

        const statusFields = itemId
          ? await this.resolveItemStatusFieldsFromItemApi(itemId)
          : {};
        const sftpRequestType = resolveVerifiedUploadRequestType(
          statusFields.itemStatus,
          'SFTP',
        );
        const sftpPayload: any = {
          type: sftpRequestType,
          additional_info: {
            sftpHost: this.sftpForm.value.sftpHost || '',
            sftpPort: this.sftpForm.value.port || '',
            sftpUser: this.sftpForm.value.username || '',
            sftpPass: this.sftpForm.value.password || '',
            remotePath: this.sftpForm.value.remotePath || '',
            localPath: this.sftpForm.value.localFilePath || '',
            description: this.sftpForm.value.optionalNotes || ''
          },
          asset_id: itemId
        };

        console.log('Calling createDataUploadRequest for SFTP:', sftpPayload);
        const sftpResponse = await this.authApiService.createDataUploadRequest(sftpPayload);
        console.log('SFTP upload request created successfully:', sftpResponse);

        if (itemId) {
          const updatePayload = await this.buildCreateAssetPayload(formData);
          updatePayload.id = itemId;
          Object.assign(updatePayload, statusFields);
          updatePayload.itemStatus = sftpRequestType;
          await this.catalogueApiService.updateItem(updatePayload);
        }

        // Store request type and dataset ID for success modal
        this.requestType = 'SFTP';
        if (itemId) {
          this.createdDatasetId = itemId;
        }
      } else if (this.isApiIntegrationSelected()) {
        // If API Integration selected, save API config via createDataUploadRequest
        if (this.apiIntegrationForm.invalid) {
          Object.keys(this.apiIntegrationForm.controls).forEach(key => {
            this.apiIntegrationForm.get(key)?.markAsTouched();
          });
          this.toasterService.error('Please fill all required API Integration fields.');
          return;
        }

        const statusFields = itemId
          ? await this.resolveItemStatusFieldsFromItemApi(itemId)
          : {};
        const apiRequestType = resolveVerifiedUploadRequestType(
          statusFields.itemStatus,
          'API',
        );
        const apiPayload: any = {
          type: apiRequestType,
          additional_info: {
            sourceName: this.apiIntegrationForm.value.dataSourceName || '',
            apiBaseUrl: this.apiIntegrationForm.value.apiBaseUrl || '',
            endpointPath: this.apiIntegrationForm.value.endpointPath || '',
            httpMethod: this.apiIntegrationForm.value.httpMethod || '',
            headers: this.apiIntegrationForm.value.customHeaders || '',
            queryParams: this.apiIntegrationForm.value.queryParameters || '',
            bodyPayload: this.apiIntegrationForm.value.bodyPayload || '',
            authDetails: this.apiIntegrationForm.value.authenticationDetails || '',
            authType: this.apiIntegrationForm.value.authenticationType || '',
            responseFormat: this.apiIntegrationForm.value.responseFormat || '',
            dataPath: this.apiIntegrationForm.value.dataPathInResponse || '',
            schedule: this.apiIntegrationForm.value.expectedPullFrequency || '',
            notes: this.apiIntegrationForm.value.additionalNotes || '',
          },
          asset_id: itemId,
        };

        console.log('Calling createDataUploadRequest for API Integration:', apiPayload);
        const apiResponse = await this.authApiService.createDataUploadRequest(apiPayload);
        console.log('API Integration upload request created successfully:', apiResponse);

        if (itemId) {
          const updatePayload = await this.buildCreateAssetPayload(formData);
          updatePayload.id = itemId;
          Object.assign(updatePayload, statusFields);
          updatePayload.itemStatus = apiRequestType;
          await this.catalogueApiService.updateItem(updatePayload);
        }

        // Store request type and dataset ID for success modal
        this.requestType = 'API';
        if (itemId) {
          this.createdDatasetId = itemId;
        }
      } else if (this.isUploadFileSelected() && this.uploadedFiles.length > 0) {
        // If Upload File selected and files are uploaded, call process API
        console.log('Calling process API for uploaded files...');
        await this.publishDatabank();
        // publishDatabank() will show the success modal, so return early
        return;
      } else if (this.isDirectPushSelected()) {
        // If Direct Push selected, persist itemStatus and show Direct Push success modal
        console.log('Direct Push selected - showing success modal');
        if (itemId) {
          const updatePayload = await this.buildCreateAssetPayload(formData);
          updatePayload.id = itemId;
          Object.assign(updatePayload, await this.resolveItemStatusFieldsFromItemApi(itemId));
          updatePayload.itemStatus = 'Direct';
          await this.catalogueApiService.updateItem(updatePayload);
        }
        this.isDirectPushSuccessModalOpen = true;
        return;
      }

      // Show success modal for SFTP/API Integration
      this.isUploadSuccessModalOpen = true;
    } catch (error: any) {
      console.error('Error on upload-step publish:', error);
      
      const errorMessage = isModel
        ? 'Unable to process upload request for Model, Please try again later !!'
        : 'Unable to process upload request for Dataset, Please try again later !!';
      
      // Show error toaster
      this.toasterService.error(errorMessage);
    } finally {
      this.isCreatingItem = false;
    }
  }

  private async buildCreateAssetPayload(formData: any): Promise<any> {
    const isModel = this.router.url.includes('/add-model/');
    return buildAssetCataloguePayloadWithAuth(this.authApiService, {
      formData,
      isModel,
      dataDescriptors: formData.dataDescriptors,
      includeModelDataSourceUrl: true,
      defaultModelMediaUrl: formData.sampleDataUrl || 'https://iudx.io/',
    });
  }

  /** Read itemStatus / publishStatus from GET item so PUT retains backend-assigned values. */
  private async resolveItemStatusFieldsFromItemApi(
    itemId: string,
  ): Promise<{ itemStatus?: string; publishStatus?: string }> {
    try {
      const response = await this.catalogueApiService.getItemById(itemId);
      const result = response?.result ?? response?.data ?? response;
      const record = Array.isArray(result) ? result[0] : result;
      if (!record || typeof record !== 'object') {
        return {};
      }

      const itemStatus = record.itemStatus ?? record.item_status;
      const publishStatus =
        record.publishStatus ??
        record.publish_status ??
        record.publishAssets ??
        record.publish_assets;

      const fields: { itemStatus?: string; publishStatus?: string } = {};
      if (itemStatus != null && String(itemStatus).trim() !== '') {
        fields.itemStatus = String(itemStatus).trim();
      }
      if (publishStatus != null && String(publishStatus).trim() !== '') {
        fields.publishStatus = String(publishStatus).trim();
      }
      return fields;
    } catch (error) {
      console.warn('Could not load item status fields for PUT payload:', error);
      return {};
    }
  }

  /**
   * Delete an uploaded file
   * - For completed uploads: calls delete file API
   * - For in-progress uploads: calls cancel upload API
   * - For pending uploads: just removes from list
   */
  async deleteUploadedFile(fileItem: typeof this.uploadedFiles[0]): Promise<void> {
    try {
      // Get databankId
      const localStorageKey = this.getLocalStorageKey();
      const existingData = localStorage.getItem(localStorageKey);
      const formData = existingData ? JSON.parse(existingData) : {};
      const databankId = formData.itemId || formData.id || formData.item_id;

      if (!databankId) {
        throw new Error('Databank ID not found');
      }

      // If file is completed (uploaded to S3), call delete file API
      if (fileItem.status === 'completed' && fileItem.key) {
        console.log('Deleting completed file from S3:', fileItem.key);
        await this.fileApiService.deleteFile(databankId, fileItem.key);
      } 
      // If file has uploadId (upload in progress or initiated), call cancel upload API
      else if (fileItem.uploadId && fileItem.key) {
        console.log('Cancelling upload:', fileItem.uploadId);
        await this.fileApiService.cancelUpload(databankId, fileItem.uploadId, fileItem.key);
      }
      // Otherwise, just remove from list (pending files)
      else {
        console.log('Removing pending file from list');
      }

      // Remove from list
      this.uploadedFiles = this.uploadedFiles.filter(f => f !== fileItem);
      
      // Clear preview if this was the previewed file
      if (this.publishedFilesPreview) {
        this.publishedFilesPreview = null;
      }

      this.toasterService.success('File deleted successfully');
    } catch (error: any) {
      console.error('Error deleting file:', error);
      this.toasterService.error('Failed to delete file: ' + (error.message || 'Unknown error'));
    }
  }

  /**
   * Publish/Process the databank
   */
  async publishDatabank(): Promise<void> {
    try {
      this.isPublishing = true;

      // Get databankId
      const localStorageKey = this.getLocalStorageKey();
      const existingData = localStorage.getItem(localStorageKey);
      const formData = existingData ? JSON.parse(existingData) : {};
      const databankId = formData.itemId || formData.id || formData.item_id;

      if (!databankId) {
        throw new Error('Databank ID not found');
      }

      // Call process API
      const response = await this.fileApiService.processDatabank(databankId, 'all');

      console.log('Databank processed successfully:', response);

      // Check if response status is 200 OK or 202 Accepted
      if (response.status === 200 || response.status === 202) {
        // Mark dataUploadStatus as true on the organisation asset
        try {
          await this.catalogueApiService.postPublishAssets(databankId, { dataUploadStatus: true });
          console.log('dataUploadStatus set to true for asset:', databankId);
        } catch (patchError) {
          console.error('Failed to set dataUploadStatus:', patchError);
        }

        // Mark as published
        this.isPublished = true;
        
        // Store the dataset/model ID for navigation if not already stored
        if (!this.createdDatasetId) {
          const localStorageKey = this.getLocalStorageKey();
          const existingData = localStorage.getItem(localStorageKey);
          const formData = existingData ? JSON.parse(existingData) : {};
          const databankId = formData.itemId || formData.id || formData.item_id;
          if (databankId) {
            this.createdDatasetId = databankId;
          }
        }
        
        // Show success modal on 200 OK or 202 Accepted
        this.isPublishSuccessModalOpen = true;
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error: any) {
      console.error('Error publishing databank:', error);
      this.toasterService.error('Failed to publish: ' + (error.message || 'Unknown error'));
    } finally {
      this.isPublishing = false;
    }
  }

  /**
   * Load preview for the first completed file
   */
  loadPublishedFilesPreview(): void {
    const completedFile = this.uploadedFiles.find(f => f.status === 'completed' && f.previewData);
    
    if (completedFile && completedFile.previewData) {
      this.publishedFilesPreview = this.parsePreviewData(
        completedFile.previewData,
        completedFile.file.name
      );
    }
  }

  /**
   * Close publish success modal - stay on current step
   */
  closePublishSuccessModal(): void {
    this.isPublishSuccessModalOpen = false;
    // User stays on Step 4 (Upload step) after closing modal
  }

  /**
   * Handle "View Dataset/Model" button click - navigate to the created item detail page
   */
  onViewDataset(): void {
    this.closePublishSuccessModal();
    
    if (this.createdDatasetId) {
      // Navigate to detail page based on type
      if (this.isModel()) {
        // Navigate to model detail page (adjust route as needed)
        this.router.navigate(['/model', this.createdDatasetId]);
      } else {
        // Navigate to dataset detail page
        this.router.navigate(['/dataset', this.createdDatasetId]);
      }
    } else {
      console.warn('Item ID not available for navigation');
      // Fallback: navigate to list page based on type
      if (this.isModel()) {
        this.router.navigate(['/models']);
      } else {
        this.router.navigate(['/datasets']);
      }
    }
  }

  /**
   * Handle "Create New" button click - reset form and navigate to step 1
   */
  onCreateNew(): void {
    this.closePublishSuccessModal();

    // Clear all flow state so Step 1 opens completely fresh
    const localStorageKey = this.getLocalStorageKey();
    localStorage.removeItem(localStorageKey);
    sessionStorage.removeItem('contributionFlowActive');
    sessionStorage.removeItem('cameFromUploadsStep');
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && (k.startsWith('updateBaseline:datasetFormData') || k.startsWith('updateBaseline:modelFormData'))) {
        sessionStorage.removeItem(k);
      }
    }

    this.createdDatasetId = null;

    const currentUrl = this.router.url;
    let navigateRoute: string;

    if (currentUrl.includes('/org-admin/')) {
      navigateRoute = currentUrl.includes('/add-model/') ? '/org-admin/add-model' : '/org-admin/add-dataset';
    } else {
      navigateRoute = currentUrl.includes('/add-model/')
        ? '/provider/add-contributions/add-model'
        : '/provider/add-contributions/add-dataset';
    }

    this.router.navigateByUrl(navigateRoute, { replaceUrl: true });
  }

  /**
   * Navigate to dashboard after successful publish (kept for backward compatibility)
   */
  navigateToDashboard(): void {
    this.isPublishSuccessModalOpen = false;
    this.router.navigate(['/provider/dashboard']);
  }

  onPrevious(): void {
    this.goBack();
  }
}

